/*
 *  This file global-state.service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *
 */


import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { EGlobalStateProcessId, EGlobalStateStoreNames, GLOBAL_API_URLS, hroneIndexDbConfig, IMoodSettingType, TBusinessUnits, TContestEnableDisableResponse, TCoreFeatureValidate, TDepartment, TDesignation, TFeedData, TGrades, TLevel, TReferralData, TRegion, TSubBranches, TSubDepartment, THelpdeskTicketFeedback, TTaxRegimeResponse, TWorkflowInboxSetting, TWorkforceSetupSettingResponse } from './global.constants';
import { HttpService } from 'src/app/global/services/http-service';
import { FeedPayloadState, IFeedResponseExtended, IWidgetSequnceResponse, TFeedCountResponse, TGeneralEngageSettingResponse, TReferralDataResponse, TReferralDataRequest } from 'src/app/modules/enterprise-wall/social-enterpise-wall/social-wall.model';
import { TSubscribeModuleResponse } from 'src/app/modules/team/team-dashboards/team-dashboards.model';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { TGlobalState, TGlobalStateProcessVersion } from './global.reducer';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { enterpriseEnableDisableSuccess, employeecontestEnableDisableSuccess, loadBadgeFeedSuccess, subscribeModuleSuccess, validateFeatureSuccess, workforceSettingSuccess, loadFeedCountSuccess, showRefreshFeed, loadEnterpriseSuccess, loadDepartmentsSuccess, loadSubDepartmentsSuccess, loadBusinessUnitsSuccess, loadLevelsSuccess, loadDesignationsSuccess, loadGradesSuccess, loadSubBranchesSuccess, loadBranchesSuccess, loadRegionsSuccess, loadCompaniesSuccess, loadCountriesSuccess, loadStatesSuccess, loadCitiesSuccess, loadSocialWallWidgetSuccess, showRefreshRecruiterAccessSetting, loadRecruiterAccessSettingSuccess, refreshRecruitmentGeneralSetting, loadRecruitmentGeneralSettingSuccess, loadGeneralSettingsSuccess, loadJobOpeningSuccess, showRefreshJobOpening, loadOneAIFeatureSuccess, loadTaxRegimeSuccess, loadTimeOfficeExtensionSuccess, loadMoodbotDetailSuccess, loadMoodbotReasionListSuccess, loadPayrollGeneralSettingSuccess, loadWorkflowInboxSettingSuccess, loadHelpdeskTicketFeedbackSuccess, loadLegoOrgIdSuccess } from './global.action';
import { LogonUserDetailModel, PayrollGeneralSettingModel } from 'src/app/global/models/common.models';
import { MomentService } from 'src/app/global/services/moment.service';
import { StateModel, CityModel, CountryModel, CompanyDataModel } from 'src/app/modules/onboarding-config/state-management/onboarding.models';
import { TGeneralSettingAPIResponse, TRecruiterAccessSettingAPIResponse } from 'src/app/global/models/recruitment-module-independent.model';
import { LocationType } from 'src/app/modules/processes/engage/events-management/events-management.model';
import { UserIdentifierUpdateService } from 'src/app/global/services/user-identifier-update.service';
import { FeatureModel } from 'src/app/modules/core/integaration/one-ai/one-ai.model';
import { TExtensionSetting } from 'src/app/modules/core/time-office/time-office-setting/time-office-setting/time-office-setting.model';
import { IMoodReason } from 'src/app/modules/core/engage/moodbot-setting/moodbot.service';

@Injectable({
  providedIn: 'root'
})
export class HroneGlobalStateService {
  deleteDatabaseSuccess = false;
  versionhash = new Map<number, string>();
  private validateFeature$ = new BehaviorSubject<Map<number, TCoreFeatureValidate> | null>(null);
  private isFeatureValidationCalled = false;
  private oneAIFeature$ = new BehaviorSubject<FeatureModel[] | null>(null);
  private isOneAIFeatureCalled = false;
  private readonly workforceSetupSettings$: Subject<TWorkforceSetupSettingResponse[] | null> = new Subject();
  private _isWorkforceSettingCalled = false;
  public get isWorkforceSettingCalled() {
    return this._isWorkforceSettingCalled;
  }
  public set isWorkforceSettingCalled(value) {
    this._isWorkforceSettingCalled = value;
  }
  readonly VERSION_DETAIL = '/api/core/DataVersioning/VersionDetail' as const;
  constructor(
    private readonly http: HttpService,
    public indexdb: NgxIndexedDBService,
    public store: Store<{ hroneGlobal: TGlobalState }>,
    private _moment: MomentService,
    private _identifierUpdateService: UserIdentifierUpdateService
  ) { }

  handleVersionDetails(value: { currentVersion: string; processId: number; }[]) {
    value.forEach(({ currentVersion, processId }) => {
      this.versionhash.set(processId, currentVersion);
    });

  }

  getVersionDetails(): Observable<Array<{ currentVersion: string, processId: number }>> {
    const bs = new BehaviorSubject([] as Array<{ currentVersion: string, processId: number }>);
    const authStr = localStorage.getItem('authlogin')
    return authStr ? this.http.Get(this.VERSION_DETAIL) : bs.asObservable();
  }
  private fetchEnterPriseContestEnableDisable(): Observable<TContestEnableDisableResponse[]> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.EnterpriseContest].CONTEST_ENABLE_DISABLE);
  }
  private fetchEmployeeContestEnableDisable(id: number): Observable<{ disabled: boolean }> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.EmployeeContest].EMPLOYEECONTESTENABLEDISABLESTATUS}/${id}`);
  }


  private fetchBadgeFeed(payloadState: FeedPayloadState): Observable<Array<IFeedResponseExtended>> {
    return this.http.Post(GLOBAL_API_URLS[EGlobalStateProcessId.Feed].BADGE_FEED, payloadState);
  }
  private fetchFeedCount(): Observable<TFeedCountResponse> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.Feed].FEED_COUNT);
  }


  private fetchSubscriberModules(): Observable<TSubscribeModuleResponse> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.SubscriberModules].SUBSCRIBE_MODULE);
  }


  private fetchWorkforceSetupSetting(settingId: number, activeStatus: string): Observable<TWorkforceSetupSettingResponse> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.WorkforceSetupSetting].WORKFORCE_SETUP_SETTING}/${settingId}/${activeStatus}`);
  }


  private fetchAllWorkforceSetupSettings(): Observable<Array<TWorkforceSetupSettingResponse>> {
    this.isWorkforceSettingCalled = true;
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.WorkforceSetupSetting].WORKFORCE_SETUP_SETTING_ALL}`).pipe(
      tap(() => {
        this.isWorkforceSettingCalled = false;
      })
    );
  }


  private fetchFeatureValidation(): Observable<TCoreFeatureValidate[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.ValidateFeature].VALIDATE_FEATURE}`);
  }

  private fetchCountryList(): Observable<CountryModel[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.Country].COUNTRY}`);
  }

  private fetchStateList(): Observable<StateModel[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.State].STATE}?status=0,1`);
  }

  private fetchCitiesList(): Observable<CityModel[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.City].CITY}?status=0,1`)
  }

  private fetchLevelList(): Observable<TLevel[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.Level].LEVEL}`)
  }

  private fetchDepartmentList(): Observable<TDepartment[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.Departments].DEPARTMENTS}`)
  }
  private fetchSubDepartmentList(): Observable<TSubDepartment[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.SubDepartment].SUB_DEPARTMENT}`)
  }
  private fetchBusinessUnitList(): Observable<TBusinessUnits[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.BusinessUnit].BUSINESS_UNIT}`)
  }
  private fetchDesignationList(): Observable<TDesignation[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.Designation].DESIGNATION}`)
  }
  private fetchGradeList(): Observable<TGrades[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.Grade].GRADE}`)
  }
  private fetchCompaniesList(): Observable<CompanyDataModel[]> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.Company].COMPANY)
  }
  private fetchRegionList(): Observable<TRegion[]> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.Region].REGION)
  }
  private fetchBranchesList(): Observable<LocationType[]> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.Branch].BRANCH)
  }
  private fetchSubBranchesList(): Observable<TSubBranches[]> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.SubBranch].SUB_BRANCH)
  }
  private fetchEnterPrise(): Observable<any> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.Enterprise].ENTERPRISE)
  }
  private fetchLegoOrgId(): Observable<string> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.Enterprise].LEGO_ORG_ID)
  }
  private fetchGeneralSetting(): Observable<any> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.GeneralSettings].GENERAL_SETTINGS)
  }
  private fetchOneAIFeatureList(): Observable<FeatureModel[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.OneAIFeature].GET_FEATURE_LIST}?featureId=263`);
  }
  private fetchTaxRegime(): Observable<TTaxRegimeResponse> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.TaxRegime].TAX_REGIME_SHOW);
  }
  private fetchTimeOfficeExtension():Observable<TExtensionSetting[]>{
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.TimeOfficeExtension].TIME_OFFICE_EXTENSION)
  }
  private fetchMoodbotDetail():Observable<IMoodSettingType[]>{
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.MoodbotDetail].MOODBOT_DETAILS)
  }
  private fetchMoodbotReasionList():Observable<IMoodReason[]>{
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.MoodbotReasionList].MOODMOT_REASON_LIST)
  }
  private fetchPayrollGeneralSetting(): Observable<PayrollGeneralSettingModel> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.PayrollGeneralSetting].PAYROLL_GENERAL_SETTING);
  }
  private fetchHelpdeskTicketFeedback(employeeId: number): Observable<THelpdeskTicketFeedback[]> {
    return this.http.Get(`${GLOBAL_API_URLS[EGlobalStateProcessId.HelpdeskTicketFeedback].TICKET_FEEDBACK}/${employeeId}`);
  }
  private fetchWorkflowInboxSettingList(): Observable<TWorkflowInboxSetting[]> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.WorkflowInboxSetting].WORKFLOW_INBOX_SETTING_LIST);
  }
  contestEnableDisable(): Observable<TContestEnableDisableResponse[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TContestEnableDisableResponse[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.EnterpriseContest).pipe(
      switchMap(
        (cache) => {
          if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
            return of(cache.data)
          }
          return this.fetchEnterPriseContestEnableDisable().pipe(
            tap(data => {
              this.store.dispatch(enterpriseEnableDisableSuccess({ data, storename: EGlobalStateStoreNames.EnterpriseContest, versionId: this.versionhash.get(EGlobalStateProcessId.EnterpriseContest) ?? "1", domainCode, employeeId }))
            })
          );
        }
      ),
      catchError(() => {
        return this.fetchEnterPriseContestEnableDisable().pipe(
          tap(data => {
            this.store.dispatch(enterpriseEnableDisableSuccess({ data, storename: EGlobalStateStoreNames.EnterpriseContest, versionId: this.versionhash.get(EGlobalStateProcessId.EnterpriseContest) ?? "1", domainCode, employeeId }))
          })
        );
      })
    )

  }
  contestEmployeeEnableDisable(id: number): Observable<{ disabled: boolean }> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: { disabled: boolean },
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.EmployeeContest).pipe(
      switchMap(
        (cache) => {
          if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date) && 'disabled' in cache.data) {
            return of(cache.data)
          }
          return this.fetchEmployeeContestEnableDisable(id).pipe(
            tap(data => {
              this.store.dispatch(employeecontestEnableDisableSuccess({ data, storename: EGlobalStateStoreNames.EmployeeContest, versionId: this.versionhash.get(EGlobalStateProcessId.EmployeeContest) ?? "1", domainCode, employeeId }))
            })
          );
        }
      ),
      catchError(() => {
        return this.fetchEmployeeContestEnableDisable(id).pipe(
          tap(data => {
            this.store.dispatch(employeecontestEnableDisableSuccess({ data, storename: EGlobalStateStoreNames.EmployeeContest, versionId: this.versionhash.get(EGlobalStateProcessId.EmployeeContest) ?? "1", domainCode, employeeId }))
          })
        );
      })
    )

  }
  getBadgeFeed(payloadState: FeedPayloadState, employeeId: number, domainCode: string, refreshFeedSignalR: boolean | undefined): Observable<Array<IFeedResponseExtended>> {
    if (refreshFeedSignalR) {
      return this.fetchBadgeFeed(payloadState).pipe(
        tap(data => {
          this.store.dispatch(loadBadgeFeedSuccess({ data, storename: EGlobalStateStoreNames.Feed, versionId: this.versionhash.get(EGlobalStateProcessId.Feed) ?? "1", domainCode, employeeId }))
        }),
      );
    }
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TFeedData,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Feed).pipe(
      switchMap(
        (cache) => {
          const versionStr = this.versionhash.get(cache.processId) ?? "1.0";
          const cacheVersionStr = cache?.data?.feedCards?.versionId ?? "1.0";
          const [currMajor, currMinor] = versionStr.split(".").map(item => Number(item));
          const [cacheMajor, cacheMinor] = cacheVersionStr.split(".").map(item => Number(item));
          const minorDiff = Math.abs(currMinor - cacheMinor);
          const isCacheValid = currMajor === cacheMajor && minorDiff < 5;
          const isGeneralShiftStartTime = this._moment.isTimeBetweenStartAndEnd(8, 11, 0, 0, 'minutes', '[]')
          this.store.dispatch(showRefreshFeed({ show: minorDiff > 0 && (isCacheValid || isGeneralShiftStartTime) }));
          if (cache && cache?.data?.feedCards?.cards?.length && cache.employeeId === employeeId &&
            domainCode === cache.domainCode && (isCacheValid || isGeneralShiftStartTime)) {
            return of(cache.data.feedCards.cards)
          }

          return this.fetchBadgeFeed(payloadState).pipe(
            tap(data => {
              this.store.dispatch(loadBadgeFeedSuccess({ data, storename: EGlobalStateStoreNames.Feed, versionId: this.versionhash.get(EGlobalStateProcessId.Feed) ?? "1", domainCode, employeeId }))
            })
          );
        }
      ),
      catchError(() => {
        return this.fetchBadgeFeed(payloadState).pipe(
          tap(data => {
            this.store.dispatch(loadBadgeFeedSuccess({ data, storename: EGlobalStateStoreNames.Feed, versionId: this.versionhash.get(EGlobalStateProcessId.Feed) ?? "1", domainCode, employeeId }))
          })
        );
      })
    )

  }
  getFeedCount(): Observable<TFeedCountResponse> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TFeedData,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Feed).pipe(
      switchMap(
        (cache) => {
          const majorVersionMatch = this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date);
          const subVersionMatch = this.shouldGetFromCache(cache?.data.countData?.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date);
          if (cache && cache?.data?.countData?.countInfo && majorVersionMatch && subVersionMatch) {
            return of(cache.data.countData.countInfo)
          }
          return this.fetchFeedCount().pipe(
            tap(data => {
              this.store.dispatch(loadFeedCountSuccess({ data, storename: EGlobalStateStoreNames.Feed, versionId: this.versionhash.get(EGlobalStateProcessId.Feed) ?? "1", domainCode, employeeId }))
            })
          );
        }
      ),
      catchError(() => {
        return this.fetchFeedCount().pipe(
          tap(data => {
            this.store.dispatch(loadFeedCountSuccess({ data, storename: EGlobalStateStoreNames.Feed, versionId: this.versionhash.get(EGlobalStateProcessId.Feed) ?? "1", domainCode, employeeId }))
          })
        );
      })
    );
  }

  subscribeModule(): Observable<TSubscribeModuleResponse> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TSubscribeModuleResponse,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.SubscriberModules).pipe(
      switchMap(
        (cache) => {

          if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {

            return of(cache.data)
          }
          return this.fetchSubscriberModules().pipe(
            tap(data => {
              this.store.dispatch(subscribeModuleSuccess({ data, storename: EGlobalStateStoreNames.SubscriberModules, versionId: this.versionhash.get(EGlobalStateProcessId.SubscriberModules) ?? "1", domainCode, employeeId }))
            })
          );
        }
      ),
      catchError(() => {
        return this.fetchSubscriberModules()
      })
    )

  }


  logOnUserDetails(): LogonUserDetailModel {
    const userInLocalStoratge = localStorage.getItem('logOnUserDetails');
    return userInLocalStoratge ? JSON.parse(userInLocalStoratge) : { employeeId: 0, domainCode: "" } as unknown as LogonUserDetailModel;
  }

  getWorkforceSetupSetting(settingId: number, activeStatus: '0,1' | '0' | '1' = '0,1'): Observable<TWorkforceSetupSettingResponse | null> {

    const { employeeId, domainCode } = this.logOnUserDetails();
    const sortedActiveStatus = activeStatus ? activeStatus
      .split(',')
      .sort((a, b) => Number(a) - Number(b))
      .join(',') : null;

    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: Map<number, TWorkforceSetupSettingResponse>,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.WorkforceSetupSetting).pipe(
      switchMap(cache => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          const activeInactive = cache.data.get(settingId);
          if (!activeInactive) {
            return this.fetchWorkforceSetupSetting(settingId, activeStatus)
          }
          else {

            if (sortedActiveStatus === '0,1') {
              return of(activeInactive || null);
            } else if (sortedActiveStatus === '0') {
              const result = activeInactive?.isActive ? null : activeInactive;
              return of(result);
            } else if (sortedActiveStatus === '1') {
              const result = activeInactive?.isActive ? activeInactive : null;
              return of(result);
            }
          }

          return of(null);
        }

        return this.fetchAndCacheAllWorkforceSetupSettings().pipe(
          map((data: TWorkforceSetupSettingResponse[]) => {
            const newData = new Map<number, TWorkforceSetupSettingResponse>();
            let setting: TWorkforceSetupSettingResponse | null = null;

            data.forEach(s => {
              newData.set(s.settingId, s);
              if (s.settingId === settingId) {
                setting = s;
              }
            });

            this.store.dispatch(workforceSettingSuccess({
              data: newData,
              storename: EGlobalStateStoreNames.WorkforceSetupSetting,
              versionId: this.versionhash.get(EGlobalStateProcessId.WorkforceSetupSetting) ?? "1",
              domainCode,
              employeeId
            }));

            return setting;
          }),
          catchError(() => {
            return this.fetchAllWorkforceSetupSettings().pipe(
              map((data: TWorkforceSetupSettingResponse[]) => {
                this.workforceSetupSettings$.next(data);
                this.isWorkforceSettingCalled = false;
                const newData = new Map<number, TWorkforceSetupSettingResponse>();
                let setting: TWorkforceSetupSettingResponse | null = null;

                data.forEach(s => {
                  newData.set(s.settingId, s);
                  if (s.settingId === settingId) {
                    setting = s;
                  }
                });

                this.store.dispatch(workforceSettingSuccess({
                  data: newData,
                  storename: EGlobalStateStoreNames.WorkforceSetupSetting,
                  versionId: this.versionhash.get(EGlobalStateProcessId.WorkforceSetupSetting) ?? "1",
                  domainCode,
                  employeeId
                }));

                return setting;
              }),
            )
          })
        );
      }),
      catchError(() => {
        return this.fetchAndCacheAllWorkforceSetupSettings().pipe(
          map((data: TWorkforceSetupSettingResponse[]) => {
            const newData = new Map<number, TWorkforceSetupSettingResponse>();
            let setting: TWorkforceSetupSettingResponse | null = null;

            data.forEach(s => {
              newData.set(s.settingId, s);
              if (s.settingId === settingId) {
                setting = s;
              }
            });

            this.store.dispatch(workforceSettingSuccess({
              data: newData,
              storename: EGlobalStateStoreNames.WorkforceSetupSetting,
              versionId: this.versionhash.get(EGlobalStateProcessId.WorkforceSetupSetting) ?? "1",
              domainCode,
              employeeId
            }));

            return setting;
          })
        );
      })
    );

  }

  getWorkforceSetupSettings(
    settingIds: number[],
    activeStatus: '0,1' | '0' | '1' = '0,1'
  ): Observable<Map<number, TWorkforceSetupSettingResponse | null>> {
    const uniqueIds = [...new Set(settingIds)];
    if (!uniqueIds.length) {
      return of(new Map());
    }

    const seedId = uniqueIds[0];
    return this.getWorkforceSetupSetting(seedId, activeStatus).pipe(
      take(1),
      switchMap(() => {
        const requests: Record<string, Observable<TWorkforceSetupSettingResponse | null>> = {};
        uniqueIds.forEach((id) => {
          requests[String(id)] = this.getWorkforceSetupSetting(id, activeStatus).pipe(take(1));
        });
        return forkJoin(requests).pipe(
          map((results) => {
            const settingsMap = new Map<number, TWorkforceSetupSettingResponse | null>();
            uniqueIds.forEach((id) => {
              settingsMap.set(id, results[String(id)] ?? null);
            });
            return settingsMap;
          })
        );
      })
    );
  }

  validateFeature(featureId: number): Observable<TCoreFeatureValidate> {
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: Map<number, TCoreFeatureValidate>,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.ValidateFeature).pipe(
      switchMap((cache) => {
        if (this.shouldGetFromCache(
          cache.versionId,
          EGlobalStateProcessId.ValidateFeature,
          cache.employeeId,
          cache.domainCode,
          cache.date
        )) {
          const validity = cache.data.get(featureId);
          return of(validity ?? { featureId, isActive: 0 } as unknown as TCoreFeatureValidate);
        }

        return this.fetchAndCacheFeatureValidation().pipe(
          map((data) => {
            const feature = data.get(featureId);
            return feature ?? { featureId, isActive: 0 } as unknown as TCoreFeatureValidate;
          })
        );
      }),
      catchError(() => {
        return this.fetchAndCacheFeatureValidation().pipe(
          map((data) => {
            const feature = data.get(featureId);
            return feature ?? { featureId, isActive: 0 } as unknown as TCoreFeatureValidate;
          })
        );
      })
    );
  }

  private fetchAndCacheFeatureValidation(): Observable<Map<number, TCoreFeatureValidate>> {
    if (!this.isFeatureValidationCalled) {
      this.isFeatureValidationCalled = true;
      this.validateFeature$.next(null);

      return this.fetchFeatureValidation().pipe(
        map((data) => {
          const mappedData = this.mapFeaturesById(data);
          this.validateFeature$.next(mappedData);

          this.store.dispatch(validateFeatureSuccess({
            data: mappedData,
            storename: EGlobalStateStoreNames.ValidateFeature,
            versionId: this.versionhash.get(EGlobalStateProcessId.ValidateFeature) ?? "1",
            domainCode: this.logOnUserDetails().domainCode,
            employeeId: this.logOnUserDetails().employeeId
          }));

          return mappedData;
        }),
        finalize(() => {
          this.isFeatureValidationCalled = false;
        }),
        catchError(err => throwError(() => err))
      );
    }

    return this.validateFeature$.pipe(
      filter(data => data !== null),
      take(1),
      map(data => data!)
    );
  }

  shouldGetFromCache(versionId: string | undefined,
    processID: number | undefined,
    employeeIdcache: number | undefined,
    domainCodecache: string | undefined, date: string | Date | undefined): boolean {
    const { employeeId, domainCode } = this.logOnUserDetails();
    if (!processID || !date) return false;
    return employeeIdcache === employeeId &&
      domainCode === domainCodecache &&
      this.versionhash.get(processID) === versionId
    // asked to comment by kashish sir.
    // &&
    // this._moment.getMomentFromDate(new Date()).diff(this._moment.getMomentFromDate(date),'hours') < 8;
  }
  private fetchAndCacheAllWorkforceSetupSettings(): Observable<TWorkforceSetupSettingResponse[]> {
    if (!this.isWorkforceSettingCalled) {
      this.workforceSetupSettings$.next(null);
      return this.fetchAllWorkforceSetupSettings().pipe(
        map((data) => {
          this.workforceSetupSettings$.next(data);
          return data;
        }),
        finalize(() => {
          this.isWorkforceSettingCalled = false;
        })
      )
    }

    return this.workforceSetupSettings$.pipe(
      filter(v => v !== null),
      take(1),
    ) as unknown as Observable<TWorkforceSetupSettingResponse[]>;
  }



  private checkIfStoreExists(storeName: string): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const version = hroneIndexDbConfig.version;
      const dbName = hroneIndexDbConfig.name;
      const request = indexedDB.open(dbName, version);

      request.onsuccess = () => {
        const db = request.result;
        const exists = db.objectStoreNames.contains(storeName);
        db.close();
        subscriber.next(exists);
        subscriber.complete();
      };
      request.onupgradeneeded = () => {
        console.error({ onupgradeneeded: "onupgradeneeded" }, new Date())
        const targetSchema = hroneIndexDbConfig.objectStoresMeta.find(meta => meta.store === storeName);
        if (!targetSchema) return;
        const db = request.result;
        if (!db.objectStoreNames.contains(storeName)) {
          const objectStore = db.createObjectStore(storeName, targetSchema.storeConfig);
          targetSchema.storeSchema.forEach((index) => {
            objectStore.createIndex(index.name, index.keypath, index.options || {});
          });
          console.info(`Object store "${storeName}" and its indexes created successfully.`);
        }
      }
      request.onerror = () => {
        console.error('Error checking store existence:', request.error);
        subscriber.error(request.error);
      };
    });
  }

  private ensureStoreExists(storeName: string): Observable<void> {
    return this.checkIfStoreExists(storeName).pipe(
      switchMap((exists) => {
        if (exists) {
          this.deleteDatabaseSuccess = false;

        } else if (!this.deleteDatabaseSuccess) {
          this.indexdb.deleteDatabase().subscribe({
            next: () => {
              this.deleteDatabaseSuccess = true;
            },
            error: (error) => {
              console.error(error);

            }
          });

        }
        return of(void 0);
      }),
      catchError((err) => {
        console.error(`Error ensuring store ${storeName} exists:`, err);
        return throwError(() => new Error(`Failed to ensure store ${storeName} exists`));
      })
    );
  }

  getEntryByStoreNameAndKey<T>(storeName: string, key: string | number): Observable<T> {
    return this.ensureStoreExists(storeName).pipe(
      switchMap(() => this.indexdb.getByID<T>(storeName, key)),
      catchError((err) => {
        console.error(`Error getting entry by key ${key} in store ${storeName}:`, err);
        throw new Error(`Failed to retrieve entry with key ${key} from store ${storeName}`);
      })
    );
  }

  getAllEntries<T = any>(storeName: string): Observable<T[]> {
    return this.ensureStoreExists(storeName).pipe(
      switchMap(() => this.indexdb.getAll<T>(storeName)),
      catchError((err) => {
        console.error(`Error getting all entries from store ${storeName}:`, err);
        throw new Error(`Failed to retrieve entries from store ${storeName}`);
      })
    );
  }

  updateEntry<T>(storeName: string, data: T): Observable<T> {
    return this.ensureStoreExists(storeName).pipe(
      switchMap(() => this.indexdb.update(storeName, { ...data, date: new Date() })),
      catchError((err) => {
        console.error(`Error updating entry in store ${storeName}:`, err);
        throw new Error(`Failed to update entry in store ${storeName}`);
      })
    );
  }

  addEntry<T>(storeName: string, data: T): Observable<T> {
    return this.ensureStoreExists(storeName).pipe(
      switchMap(() => this.indexdb.add(storeName, { ...data, date: new Date() })),
      catchError((err) => {
        console.error(`Error adding entry to store ${storeName}:`, err);
        throw new Error(`Failed to add entry to store ${storeName}`);
      })
    );
  }

  deleteEntryByKey(storeName: string, key: string | number): Observable<any> {
    return this.ensureStoreExists(storeName).pipe(
      switchMap(() => this.indexdb.delete(storeName, key)),
      catchError((err) => {
        console.error(`Error deleting entry with key ${key} from store ${storeName}:`, err);
        return throwError(() => new Error(`Failed to delete entry with key ${key} from store ${storeName}`));
      })
    );
  }

  mapFeaturesById(features: TCoreFeatureValidate[]): Map<number, TCoreFeatureValidate> {
    return new Map(features.map(feature => [feature.featureId, feature]));
  }

  getLegoOrgId(hardRefress: boolean = false): Observable<string> {
    if (hardRefress) return this.refreshLegoOrgId();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: { legoOrgId?: string },
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Enterprise).pipe(
      switchMap((cache) => {
        if (cache && cache.data?.legoOrgId && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data.legoOrgId);
        }
        return this.refreshLegoOrgId();
      }),
      catchError(() => this.refreshLegoOrgId())
    );
  }

  refreshLegoOrgId(): Observable<string> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.fetchLegoOrgId().pipe(
      tap(data => {
        this.store.dispatch(loadLegoOrgIdSuccess({
          data,
          storename: EGlobalStateStoreNames.Enterprise,
          versionId: this.versionhash.get(EGlobalStateProcessId.Enterprise) ?? "1",
          domainCode,
          employeeId
        }));
      })
    );
  }

  getEnterprise(): Observable<any> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: any,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Enterprise).pipe(
      switchMap((cache) => {
        if (cache && cache?.data?.enterprise && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data.enterprise);
        }
        return this.fetchEnterPrise().pipe(
          tap(data => {
            // Update identifier in localStorage logOnUserDetails
            if (data && data.organizationKeyOne && data.organizationKeyTwo) {
              const userDetails = this.logOnUserDetails();
              if (userDetails && userDetails.hasOwnProperty('identifier') && userDetails.identifier) {
                userDetails.identifier.organizationKeyOne = data.organizationKeyOne;
                userDetails.identifier.organizationKeyTwo = data.organizationKeyTwo;
                localStorage.setItem('logOnUserDetails', JSON.stringify(userDetails));
                this._identifierUpdateService.updateIdentifiers(data.organizationKeyOne, data.organizationKeyTwo);
              }
            }

            this.store.dispatch(loadEnterpriseSuccess({
              data,
              storename: EGlobalStateStoreNames.Enterprise,
              versionId: this.versionhash.get(EGlobalStateProcessId.Enterprise) ?? "1",
              domainCode,
              employeeId
            }));
          }),
        );
      }),
      catchError(() => {
        return this.fetchEnterPrise().pipe(
          tap(data => {
            // Update identifier in localStorage logOnUserDetails
            if (data && data.organizationKeyOne && data.organizationKeyTwo) {
              const userDetails = this.logOnUserDetails();
              if (userDetails && userDetails.hasOwnProperty('identifier') && userDetails.identifier) {
                userDetails.identifier.organizationKeyOne = data.organizationKeyOne;
                userDetails.identifier.organizationKeyTwo = data.organizationKeyTwo;
                localStorage.setItem('logOnUserDetails', JSON.stringify(userDetails));
                this._identifierUpdateService.updateIdentifiers(data.organizationKeyOne, data.organizationKeyTwo);
              }
            }

            this.store.dispatch(loadEnterpriseSuccess({
              data,
              storename: EGlobalStateStoreNames.Enterprise,
              versionId: this.versionhash.get(EGlobalStateProcessId.Enterprise) ?? "1",
              domainCode,
              employeeId
            }));
          }),
        );
      })
    );
  }

  getTaxRegime(): Observable<TTaxRegimeResponse | null> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TTaxRegimeResponse | null,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.TaxRegime).pipe(
      switchMap((cache) => {
        if (cache && cache.data && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data);
        }
        return this.fetchTaxRegime().pipe(
          tap(data => {
            this.store.dispatch(loadTaxRegimeSuccess({
              data,
              storename: EGlobalStateStoreNames.TaxRegime,
              versionId: this.versionhash.get(EGlobalStateProcessId.TaxRegime) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      }),
      catchError(() => {
        return this.fetchTaxRegime().pipe(
          tap(data => {
            this.store.dispatch(loadTaxRegimeSuccess({
              data,
              storename: EGlobalStateStoreNames.TaxRegime,
              versionId: this.versionhash.get(EGlobalStateProcessId.TaxRegime) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      })
    );
  }

  updateTaxRegimeCacheAfterSave(taxRegime: string): void {
    const { employeeId, domainCode } = this.logOnUserDetails();
    this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TTaxRegimeResponse | null,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.TaxRegime).pipe(take(1)).subscribe(cache => {
      if (!cache?.data) return;
      this.store.dispatch(loadTaxRegimeSuccess({
        data: { ...cache.data, taxRegime, showTaxRegime: false },
        storename: EGlobalStateStoreNames.TaxRegime,
        versionId: cache.versionId ?? this.versionhash.get(EGlobalStateProcessId.TaxRegime) ?? "1",
        domainCode,
        employeeId
      }));
    });
  }

  getPayrollGeneralSetting(): Observable<PayrollGeneralSettingModel | null> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: PayrollGeneralSettingModel | null,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.PayrollGeneralSetting).pipe(
      switchMap((cache) => {
        if (cache && cache.data && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data);
        }
        return this.fetchPayrollGeneralSetting().pipe(
          tap(data => {
            this.store.dispatch(loadPayrollGeneralSettingSuccess({
              data,
              storename: EGlobalStateStoreNames.PayrollGeneralSetting,
              versionId: this.versionhash.get(EGlobalStateProcessId.PayrollGeneralSetting) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      }),
      catchError(() => {
        return this.fetchPayrollGeneralSetting().pipe(
          tap(data => {
            this.store.dispatch(loadPayrollGeneralSettingSuccess({
              data,
              storename: EGlobalStateStoreNames.PayrollGeneralSetting,
              versionId: this.versionhash.get(EGlobalStateProcessId.PayrollGeneralSetting) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      })
    );
  }

  refreshPayrollGeneralSetting(): Observable<PayrollGeneralSettingModel> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.fetchPayrollGeneralSetting().pipe(
      tap(data => {
        this.store.dispatch(loadPayrollGeneralSettingSuccess({
          data,
          storename: EGlobalStateStoreNames.PayrollGeneralSetting,
          versionId: this.versionhash.get(EGlobalStateProcessId.PayrollGeneralSetting) ?? "1",
          domainCode,
          employeeId
        }));
      })
    );
  }

  getDepartments(): Observable<TDepartment[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TDepartment[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Departments).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data)
        }
        return this.fetchDepartmentList().pipe(
          tap(data => {
            this.store.dispatch(loadDepartmentsSuccess({
              data,
              storename: EGlobalStateStoreNames.Departments,
              versionId: this.versionhash.get(EGlobalStateProcessId.Departments) ?? "1",
              domainCode,
              employeeId
            }));
          })
        )
      }),
      catchError(() => {
        return this.fetchDepartmentList().pipe(
          tap(data => {
            this.store.dispatch(loadDepartmentsSuccess({
              data,
              storename: EGlobalStateStoreNames.Departments,
              versionId: this.versionhash.get(EGlobalStateProcessId.Departments) ?? "1",
              domainCode,
              employeeId
            }));
          })
        )
      })
    );
  }

  getSubDepartments(): Observable<TSubDepartment[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TSubDepartment[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.SubDepartment).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data)
        }
        return this.fetchSubDepartmentList().pipe(
          tap(data => {
            this.store.dispatch(loadSubDepartmentsSuccess({
              data,
              storename: EGlobalStateStoreNames.SubDepartment,
              versionId: this.versionhash.get(EGlobalStateProcessId.SubDepartment) ?? "1",
              domainCode,
              employeeId
            }));
          })
        )
      }),
      catchError(() => {
        return this.fetchSubDepartmentList().pipe(
          tap(data => {
            this.store.dispatch(loadSubDepartmentsSuccess({
              data,
              storename: EGlobalStateStoreNames.SubDepartment,
              versionId: this.versionhash.get(EGlobalStateProcessId.SubDepartment) ?? "1",
              domainCode,
              employeeId
            }));
          })
        )
      })
    );
  }

  getBusinessUnits(): Observable<TBusinessUnits[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TBusinessUnits[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.BusinessUnit).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data)
        }
        return this.fetchBusinessUnitList().pipe(
          tap(data => {
            this.store.dispatch(loadBusinessUnitsSuccess({
              data,
              storename: EGlobalStateStoreNames.BusinessUnit,
              versionId: this.versionhash.get(EGlobalStateProcessId.BusinessUnit) ?? "1",
              domainCode,
              employeeId
            }));
          })
        )
      }),
      catchError(() => {
        return this.fetchBusinessUnitList().pipe(
          tap(data => {
            this.store.dispatch(loadBusinessUnitsSuccess({
              data,
              storename: EGlobalStateStoreNames.BusinessUnit,
              versionId: this.versionhash.get(EGlobalStateProcessId.BusinessUnit) ?? "1",
              domainCode,
              employeeId
            }));
          })
        )
      })
    );
  }

  getLevels(): Observable<TLevel[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TLevel[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Level).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data)
        }
        return this.fetchLevelList().pipe(
          tap((data) => {
            this.store.dispatch(loadLevelsSuccess({
              data,
              storename: EGlobalStateStoreNames.Level,
              versionId: this.versionhash.get(EGlobalStateProcessId.Level) ?? '1',
              domainCode,
              employeeId
            }))
          })
        )
      }),
      catchError(() => {
        return this.fetchLevelList().pipe(
          tap((data) => {
            this.store.dispatch(loadLevelsSuccess({
              data,
              storename: EGlobalStateStoreNames.Level,
              versionId: this.versionhash.get(EGlobalStateProcessId.Level) ?? '1',
              domainCode,
              employeeId
            }))
          })
        )
      })
    );
  }

  getDesignations(): Observable<TDesignation[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TDesignation[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Designation).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data)
        }
        return this.fetchDesignationList().pipe(
          tap((data) => {
            const currentVersion = this.versionhash.get(EGlobalStateProcessId.Designation) ?? "1";
            this.store.dispatch(loadDesignationsSuccess({
              data,
              storename: EGlobalStateStoreNames.Designation,
              versionId: currentVersion,
              domainCode,
              employeeId
            }));
          })
        )
      }),
      catchError(() => {
        return this.fetchDesignationList().pipe(
          tap((data) => {
            const currentVersion = this.versionhash.get(EGlobalStateProcessId.Designation) ?? "1";
            this.store.dispatch(loadDesignationsSuccess({
              data,
              storename: EGlobalStateStoreNames.Designation,
              versionId: currentVersion,
              domainCode,
              employeeId
            }));
          })
        )
      })
    );
  }

  getGrades(): Observable<any[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: any[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Grade).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data)
        }
        return this.fetchGradeList().pipe(
          tap(data => {
            const currentVersion = this.versionhash.get(EGlobalStateProcessId.Grade) ?? "1";
            this.store.dispatch(loadGradesSuccess({
              data,
              storename: EGlobalStateStoreNames.Grade,
              versionId: currentVersion,
              domainCode,
              employeeId
            }));
          }),
        );
      }),
      catchError(() => {
        return this.fetchGradeList().pipe(
          tap(data => {
            const currentVersion = this.versionhash.get(EGlobalStateProcessId.Grade) ?? "1";
            this.store.dispatch(loadGradesSuccess({
              data,
              storename: EGlobalStateStoreNames.Grade,
              versionId: currentVersion,
              domainCode,
              employeeId
            }));
          }),
        );
      })
    );
  }

  getSubBranches(): Observable<TSubBranches[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TSubBranches[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.SubBranch).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data);
        }
        return this.fetchSubBranchesList().pipe(
          tap(data => {
            this.store.dispatch(loadSubBranchesSuccess({
              data,
              storename: EGlobalStateStoreNames.SubBranch,
              versionId: this.versionhash.get(EGlobalStateProcessId.SubBranch) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      }),
      catchError(() => {
        return this.fetchSubBranchesList().pipe(
          tap(data => {
            this.store.dispatch(loadSubBranchesSuccess({
              data,
              storename: EGlobalStateStoreNames.SubBranch,
              versionId: this.versionhash.get(EGlobalStateProcessId.SubBranch) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      })
    );
  }

  getBranches(): Observable<LocationType[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: LocationType[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Branch).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data);
        }
        return this.fetchBranchesList().pipe(
          tap(data => {
            this.store.dispatch(loadBranchesSuccess({
              data,
              storename: EGlobalStateStoreNames.Branch,
              versionId: this.versionhash.get(EGlobalStateProcessId.Branch) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      }),
      catchError(() => {
        return this.fetchBranchesList().pipe(
          tap(data => {
            this.store.dispatch(loadBranchesSuccess({
              data,
              storename: EGlobalStateStoreNames.Branch,
              versionId: this.versionhash.get(EGlobalStateProcessId.Branch) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      })
    );
  }

  getRegions(): Observable<TRegion[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TRegion[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Region).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data);
        }
        return this.fetchRegionList().pipe(
          tap(data => {
            this.store.dispatch(loadRegionsSuccess({
              data,
              storename: EGlobalStateStoreNames.Region,
              versionId: this.versionhash.get(EGlobalStateProcessId.Region) ?? "1",
              domainCode,
              employeeId
            }));

          })
        );
      }),
      catchError(() => {
        return this.fetchRegionList().pipe(
          tap(data => {
            this.store.dispatch(loadRegionsSuccess({
              data,
              storename: EGlobalStateStoreNames.Region,
              versionId: this.versionhash.get(EGlobalStateProcessId.Region) ?? "1",
              domainCode,
              employeeId
            }));

          })
        );
      })
    );
  }

  getCompanies(): Observable<CompanyDataModel[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: CompanyDataModel[];
      loading: boolean;
    }>>('hronestore', EGlobalStateProcessId.Company).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data);
        }
        return this.fetchCompaniesList().pipe(
          tap(data => {
            this.store.dispatch(loadCompaniesSuccess({
              data,
              storename: EGlobalStateStoreNames.Company,
              versionId: this.versionhash.get(EGlobalStateProcessId.Company) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );

      }),
      catchError(() => {
        return this.fetchCompaniesList().pipe(
          tap(data => {
            this.store.dispatch(loadCompaniesSuccess({
              data,
              storename: EGlobalStateStoreNames.Company,
              versionId: this.versionhash.get(EGlobalStateProcessId.Company) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      })
    );
  }

  getGeneralSettings(): Observable<TGeneralEngageSettingResponse> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TSubBranches[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.GeneralSettings).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data);
        }
        return this.fetchGeneralSetting().pipe(
          tap(data => {
            this.store.dispatch(loadGeneralSettingsSuccess({
              data,
              storename: EGlobalStateStoreNames.GeneralSettings,
              versionId: this.versionhash.get(EGlobalStateProcessId.GeneralSettings) ?? "1",
              domainCode,
              employeeId
            }));
          }),
        );
      }),
      catchError(() => {
        return this.fetchGeneralSetting().pipe(
          tap(data => {
            this.store.dispatch(loadGeneralSettingsSuccess({
              data,
              storename: EGlobalStateStoreNames.GeneralSettings,
              versionId: this.versionhash.get(EGlobalStateProcessId.GeneralSettings) ?? "1",
              domainCode,
              employeeId
            }));
          }),
        );
      })
    )
  }

  getCountries(): Observable<CountryModel[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: CountryModel[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Country).pipe(
      switchMap((cache) => {
        // If we have cached data, check if we need to refresh based on version
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data.map(item => ({ ...item })));
        }

        // Only get version details if we don't have valid cached data
        return this.fetchCountryList().pipe(
          tap(data => {
            this.store.dispatch(loadCountriesSuccess({
              data,
              storename: EGlobalStateStoreNames.Country,
              versionId: this.versionhash.get(EGlobalStateProcessId.Country) ?? '1',
              domainCode,
              employeeId
            }));
          })
        )
      },
      ),
      catchError(() => {
        return this.fetchCountryList().pipe(
          tap(data => {
            this.store.dispatch(loadCountriesSuccess({
              data,
              storename: EGlobalStateStoreNames.Country,
              versionId: this.versionhash.get(EGlobalStateProcessId.Country) ?? '1',
              domainCode,
              employeeId
            }));
          })
        )
      })
    );
  }
  getStates(countryCode?: string): Observable<StateModel[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: StateModel[],
      loading: boolean
    }>>('hronestore', EGlobalStateProcessId.State).pipe(
      switchMap(
        (cache) => {
          if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
            let states = cache.data;
            if (countryCode) {
              states = cache.data.filter((item) => item.countryCode == countryCode)
            }
            return of(states);
          }
          return this.fetchStateList().pipe(
            switchMap((data) => {
              let newStates = data;
              this.store.dispatch(loadStatesSuccess({
                data,
                storename: EGlobalStateStoreNames.State,
                versionId: this.versionhash.get(EGlobalStateProcessId.State) ?? '1',
                domainCode,
                employeeId
              }))
              if (countryCode) {
                newStates = data.filter((item) => item.countryCode == countryCode);
              }
              return of(newStates)
            })
          )
        }),
      catchError(() => {
        return this.fetchStateList().pipe(
          switchMap((data) => {
            let newStates = data;
            this.store.dispatch(loadStatesSuccess({
              data,
              storename: EGlobalStateStoreNames.State,
              versionId: this.versionhash.get(EGlobalStateProcessId.State) ?? '1',
              domainCode,
              employeeId
            }))
            if (countryCode) {
              newStates = data.filter((item) => item.countryCode == countryCode);
            }
            return of(newStates)
          })
        )
      })
    )
  }

  getCities(stateCode?: string): Observable<CityModel[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: CityModel[],
      loading: boolean
    }>>('hronestore', EGlobalStateProcessId.City).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          let cities = cache.data;
          if (stateCode) {
            cities = cache.data.filter((item) => item.stateCode == stateCode);
          }
          return of(cities);
        }
        return this.fetchCitiesList().pipe(
          switchMap((data) => {
            let newCities = data;
            this.store.dispatch(loadCitiesSuccess({
              data,
              storename: EGlobalStateStoreNames.City,
              versionId: this.versionhash.get(EGlobalStateProcessId.City) ?? '1',
              domainCode,
              employeeId
            }))
            if (stateCode) {
              newCities = data.filter((item) => item.stateCode == stateCode);
            }
            return of(newCities)
          })
        )
      }),
      catchError(() => {
        return this.fetchCitiesList().pipe(
          switchMap((data) => {
            let newCities = data;
            this.store.dispatch(loadCitiesSuccess({
              data,
              storename: EGlobalStateStoreNames.City,
              versionId: this.versionhash.get(EGlobalStateProcessId.City) ?? '1',
              domainCode,
              employeeId
            }))
            if (stateCode) {
              newCities = data.filter((item) => item.stateCode == stateCode);
            }
            return of(newCities)
          })
        )
      })
    )
  }


  fetchTotalJobOpening(req_data: any): Observable<any> {
    return this.http.Post(GLOBAL_API_URLS[EGlobalStateProcessId.Referralposting].GET_TOTAL_JOB_OPENING, req_data);
  }

  getTotalJobOpening(payloadState: TReferralDataRequest, employeeId: number, domainCode: string): Observable<Array<TReferralDataResponse>> {
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TReferralData,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.Referralposting).pipe(
      switchMap(
        (cache) => {
          const versionStr = this.versionhash.get(cache.processId) ?? "1.0";
          const cacheVersionStr = cache?.data?.versionId ?? "1.0";
          const minorDiff = Math.abs(Number(versionStr) - Number(cacheVersionStr));
          this.store.dispatch(showRefreshJobOpening({ show: minorDiff > 0 }));
          if (cache && cache?.data?.data.length && cache.employeeId === employeeId &&
            domainCode === cache.domainCode && minorDiff === 0) {
            return of(cache.data.data)
          }
          return this.fetchTotalJobOpening(payloadState).pipe(
            tap(data => {
              this.store.dispatch(loadJobOpeningSuccess({ data, storename: EGlobalStateStoreNames.Referralposting, versionId: this.versionhash.get(EGlobalStateProcessId.Referralposting) ?? "1", domainCode, employeeId }))
            })
          );
        }
      ),
      catchError(() => {
        return this.fetchTotalJobOpening(payloadState).pipe(
          tap(data => {
            this.store.dispatch(loadJobOpeningSuccess({ data, storename: EGlobalStateStoreNames.Referralposting, versionId: this.versionhash.get(EGlobalStateProcessId.Referralposting) ?? "1", domainCode, employeeId }))
          })
        );
      })
    )
  }

  private fetchSocialWallWidget(): Observable<{
    themeId: string,
    isEmployeeEditable: boolean,
    employeeWidgets: Array<IWidgetSequnceResponse>
  }> {
    const url = GLOBAL_API_URLS[EGlobalStateProcessId.SocialWallWidget].GET_WIDGET_SEQUENCE;
    return this.http.Get(url).pipe(
      map(response => response as {
        themeId: string,
        isEmployeeEditable: boolean,
        employeeWidgets: Array<IWidgetSequnceResponse>
      }),
      tap({
        next: (response) => {
          if (!response) {
            console.warn('API returned null or undefined response');
          } else if (!response.employeeWidgets || !Array.isArray(response.employeeWidgets)) {
            console.warn('API response is missing employeeWidgets array:', response);
          }
        },
        error: (error) => {
          console.error('Error details:', {
            message: error.message,
            status: error.status,
            statusText: error.statusText,
            url: error.url
          });
        }
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  getSocialWallWidget(): Observable<{
    themeId: string,
    isEmployeeEditable: boolean,
    employeeWidgets: Array<IWidgetSequnceResponse>
  }> {
    const { employeeId, domainCode } = this.logOnUserDetails();

    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: {
        themeId: string,
        isEmployeeEditable: boolean,
        employeeWidgets: Array<IWidgetSequnceResponse>
      },
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.SocialWallWidget).pipe(
      switchMap((cache) => {
        if (cache && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date) && cache.data && cache.data.employeeWidgets && Array.isArray(cache.data.employeeWidgets)) {
          return of(cache.data);
        }
        return this.fetchSocialWallWidget().pipe(
          tap((data) => {
            this.store.dispatch(loadSocialWallWidgetSuccess({
              data,
              storename: EGlobalStateStoreNames.SocialWallWidget,
              versionId: this.versionhash.get(EGlobalStateProcessId.SocialWallWidget) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      }),
      catchError(() => {
        return this.fetchSocialWallWidget().pipe(
          tap((data) => {
            this.store.dispatch(loadSocialWallWidgetSuccess({
              data,
              storename: EGlobalStateStoreNames.SocialWallWidget,
              versionId: this.versionhash.get(EGlobalStateProcessId.SocialWallWidget) ?? "1",
              domainCode,
              employeeId
            }));
          })
        );
      })
    );
  }

  private fetchRecruiterAccessSetting(): Observable<TRecruiterAccessSettingAPIResponse[]> {
    return this.http.Get(GLOBAL_API_URLS[EGlobalStateProcessId.RecruiterAccessSetting].GET_RECRUITER_ACCESS_SETTING);
  }

  getRecruiterAccessSetting(): Observable<TRecruiterAccessSettingAPIResponse[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TRecruiterAccessSettingAPIResponse[],
      versionId: string,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.RecruiterAccessSetting).pipe(
      switchMap(
        (cache) => {
          const versionStr = this.versionhash.get(cache.processId) ?? "1.0";
          const cacheVersionStr = cache?.versionId ?? "1.0";
          const minorDiff = Math.abs(Number(versionStr) - Number(cacheVersionStr));
          this.store.dispatch(showRefreshRecruiterAccessSetting({ show: minorDiff > 0 }));
          if (cache && cache?.data?.length && cache.employeeId === employeeId &&
            domainCode === cache.domainCode && minorDiff === 0) {
            return of(cache.data)
          }
          return this.fetchRecruiterAccessSetting().pipe(
            tap(data => {
              this.store.dispatch(loadRecruiterAccessSettingSuccess({ data, storename: EGlobalStateStoreNames.RecruiterAccessSetting, versionId: versionStr, domainCode, employeeId }))
            })
          );
        }
      ),
      catchError(() => {
        return this.fetchRecruiterAccessSetting().pipe(
          tap(data => {
            this.store.dispatch(loadRecruiterAccessSettingSuccess({ data, storename: EGlobalStateStoreNames.RecruiterAccessSetting, versionId: this.versionhash.get(EGlobalStateProcessId.RecruiterAccessSetting) ?? "1", domainCode, employeeId }))
          })
        );
      })
    );
  }

  getRecruitmentGeneralSettingList(): Observable<TGeneralSettingAPIResponse[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TGeneralSettingAPIResponse[],
      versionId: string,
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.RecruitmentGeneralSetting).pipe(
      switchMap(
        (cache) => {
          const versionStr = this.versionhash.get(cache.processId) ?? "1.0";
          const cacheVersionStr = cache?.versionId ?? "1.0";
          const minorDiff = Math.abs(Number(versionStr) - Number(cacheVersionStr));
          this.store.dispatch(refreshRecruitmentGeneralSetting({ show: minorDiff > 0 }));
          if (cache && cache?.data?.length && cache.employeeId === employeeId &&
            domainCode === cache.domainCode && minorDiff === 0) {
            return of(cache.data)
          }
          return this.fetchGeneralSettingList().pipe(
            tap(data => {
              this.store.dispatch(loadRecruitmentGeneralSettingSuccess({ data, storename: EGlobalStateStoreNames.RecruitmentGeneralSetting, versionId: versionStr, domainCode, employeeId }))
            })
          );
        }
      ),
      catchError(() => {
        return this.fetchGeneralSettingList().pipe(
          tap(data => {
            this.store.dispatch(loadRecruitmentGeneralSettingSuccess({ data, storename: EGlobalStateStoreNames.RecruitmentGeneralSetting, versionId: this.versionhash.get(EGlobalStateProcessId.RecruitmentGeneralSetting) ?? "1", domainCode, employeeId }))
          })
        );
      })
    );
  }

  fetchGeneralSettingList(): Observable<TGeneralSettingAPIResponse[]> {
    const url = GLOBAL_API_URLS[EGlobalStateProcessId.RecruitmentGeneralSetting].GENERAL_SETTING_LIST;
    return this.http.Get(url);
  }

  getOneAIFeatureList(): Observable<FeatureModel[]> {
    const { domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: FeatureModel[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.OneAIFeature).pipe(
      switchMap((cache) => {
        const isCacheValid = !!cache && cache.domainCode === domainCode &&
          this.versionhash.get(EGlobalStateProcessId.OneAIFeature) === cache.versionId;
         if (isCacheValid) {
          return of(cache.data);
        }

        return this.fetchAndCacheOneAIFeature();
      }),
      catchError(() => {
        return this.fetchAndCacheOneAIFeature();
      })
    );
  }

  getOneAIFeature(oneAIFeatureId: number): Observable<FeatureModel | undefined> {
    return this.getOneAIFeatureList().pipe(
      map((features) => this.findOneAIFeatureById(features, oneAIFeatureId))
    );
  }

  private findOneAIFeatureById(features: FeatureModel[], oneAIFeatureId: number): FeatureModel | undefined {
    for (const feature of features) {
      if (feature.oneAIFeatureId === oneAIFeatureId) {
        return feature;
      }
      const match = feature.subFeatures?.length ? this.findOneAIFeatureById(feature.subFeatures, oneAIFeatureId) : undefined;
      if (match) {
        return match;
      }
    }
    return undefined;
  }

  private fetchAndCacheOneAIFeature(): Observable<FeatureModel[]> {
    if (!this.isOneAIFeatureCalled) {
      this.isOneAIFeatureCalled = true;
      this.oneAIFeature$.next(null);

      return this.fetchOneAIFeatureList().pipe(
        tap((data) => {
          this.oneAIFeature$.next(data);

          // Store gets its own deep copy so NgRx's dev-mode Object.freeze on the
          // dispatched action doesn't freeze the same reference handed to consumers,
          // which two-way bind (ngModel) against it.
          this.store.dispatch(loadOneAIFeatureSuccess({
            data: structuredClone(data),
            storename: EGlobalStateStoreNames.OneAIFeature,
            versionId: this.versionhash.get(EGlobalStateProcessId.OneAIFeature) ?? "1",
            domainCode: this.logOnUserDetails().domainCode,
            employeeId: 0
          }));
        }),
        finalize(() => {
          this.isOneAIFeatureCalled = false;
        }),
        catchError(err => throwError(() => err))
      );
    }

    return this.oneAIFeature$.pipe(
      filter(data => data !== null),
      take(1),
      map(data => data!)
    );
  }

  shouldGetFromCacheByDomainCode(processId:number, date?: string | Date, versionId?:string, dc?:string):boolean{
    const { domainCode } = this.logOnUserDetails();
    if(!processId || !date) return false;
    return dc === domainCode && this.versionhash.get(processId) === versionId;
  }

  getTimeofficeExtension():Observable<TExtensionSetting[]>{
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TExtensionSetting[],
      loading: boolean
    }>>('hronestore',EGlobalStateProcessId.TimeOfficeExtension).pipe(
      switchMap((cache)=>{
        if(cache && this.shouldGetFromCacheByDomainCode(cache.processId, cache.date ,cache.versionId, cache.domainCode)){
          return of(cache.data);
        }
       return this.fetchTimeOfficeExtension().pipe(
          switchMap((data) => {
            this.store.dispatch(loadTimeOfficeExtensionSuccess({
              data,
              storename: EGlobalStateStoreNames.TimeOfficeExtension,
              versionId: this.versionhash.get(EGlobalStateProcessId.TimeOfficeExtension) ?? '1',
              domainCode,
              employeeId
            }))
            return of(data);
          })
        )
      }),
      catchError(()=>{
        return this.fetchTimeOfficeExtension().pipe(
          switchMap((data) => {
            this.store.dispatch(loadTimeOfficeExtensionSuccess({
              data,
              storename: EGlobalStateStoreNames.TimeOfficeExtension,
              versionId: this.versionhash.get(EGlobalStateProcessId.TimeOfficeExtension) ?? '1',
              domainCode,
              employeeId
            }))
            return of(data);
          })
        )
      })
    )
  }
  getMoodbotDetails():Observable<IMoodSettingType[]>{
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: IMoodSettingType[],
      loading: boolean
    }>>('hronestore',EGlobalStateProcessId.MoodbotDetail).pipe(
      switchMap((cache)=>{
        if(cache && this.shouldGetFromCacheByDomainCode(cache.processId, cache.date ,cache.versionId, cache.domainCode)){
          return of(cache.data);
        }
       return this.fetchMoodbotDetail().pipe(
          switchMap((data) => {
            this.store.dispatch(loadMoodbotDetailSuccess({
              data,
              storename: EGlobalStateStoreNames.MoodbotDetail,
              versionId: this.versionhash.get(EGlobalStateProcessId.MoodbotDetail) ?? '1',
              domainCode,
              employeeId
            }))
            return of(data);
          })
        )
      }),
      catchError(()=>{
        return this.fetchMoodbotDetail().pipe(
          switchMap((data) => {
            this.store.dispatch(loadMoodbotDetailSuccess({
              data,
              storename: EGlobalStateStoreNames.MoodbotDetail,
              versionId: this.versionhash.get(EGlobalStateProcessId.MoodbotDetail) ?? '1',
              domainCode,
              employeeId
            }))
            return of(data);
          })
        )
      })
    )
  }
  getMoodbotReasonList():Observable<IMoodReason[]>{
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: IMoodReason[],
      loading: boolean
    }>>('hronestore',EGlobalStateProcessId.MoodbotReasionList).pipe(
      switchMap((cache)=>{
        if(cache && this.shouldGetFromCacheByDomainCode(cache.processId, cache.date ,cache.versionId, cache.domainCode)){
          return of(cache.data);
        }
       return this.fetchMoodbotReasionList().pipe(
          switchMap((data) => {
            this.store.dispatch(loadMoodbotReasionListSuccess({
              data,
              storename: EGlobalStateStoreNames.MoodbotReasionList,
              versionId: this.versionhash.get(EGlobalStateProcessId.MoodbotReasionList) ?? '1',
              domainCode,
              employeeId
            }))
            return of(data);
          })
        )
      }),
      catchError(()=>{
        return this.fetchMoodbotReasionList().pipe(
          switchMap((data) => {
            this.store.dispatch(loadMoodbotReasionListSuccess({
              data,
              storename: EGlobalStateStoreNames.MoodbotReasionList,
              versionId: this.versionhash.get(EGlobalStateProcessId.MoodbotReasionList) ?? '1',
              domainCode,
              employeeId
            }))
            return of(data);
          })
        )
      })
    )
  }
  getWorkflowInboxSettingList(hardRefress:boolean = false): Observable<TWorkflowInboxSetting[]> {
    if (hardRefress) return this.refreshWorkflowInboxSettingList();
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: TWorkflowInboxSetting[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.WorkflowInboxSetting).pipe(
      switchMap((cache) => {
        if (cache && cache.data?.length && this.shouldGetFromCacheByDomainCode(cache.processId, cache.date, cache.versionId, cache.domainCode)) {
          return of(cache.data);
        }
        return this.refreshWorkflowInboxSettingList();
      }),
      catchError(() => this.refreshWorkflowInboxSettingList())
    );
  }

  refreshWorkflowInboxSettingList(): Observable<TWorkflowInboxSetting[]> {
    const { employeeId, domainCode } = this.logOnUserDetails();
    return this.fetchWorkflowInboxSettingList().pipe(
      tap(data => {
        this.store.dispatch(loadWorkflowInboxSettingSuccess({
          data,
          storename: EGlobalStateStoreNames.WorkflowInboxSetting,
          versionId: this.versionhash.get(EGlobalStateProcessId.WorkflowInboxSetting) ?? "1",
          domainCode,
          employeeId
        }));
      })
    );
  }

  getHelpdeskTicketFeedback(hardRefress:boolean = false): Observable<THelpdeskTicketFeedback[]> {
    const { employeeId } = this.logOnUserDetails();
    if (hardRefress) return this.refreshHelpdeskTicketFeedback(employeeId);
    return this.getEntryByStoreNameAndKey<TGlobalStateProcessVersion<{
      data: THelpdeskTicketFeedback[],
      loading: boolean,
    }>>('hronestore', EGlobalStateProcessId.HelpdeskTicketFeedback).pipe(
      switchMap((cache) => {
        if (cache && cache.data && this.shouldGetFromCache(cache.versionId, cache.processId, cache.employeeId, cache.domainCode, cache.date)) {
          return of(cache.data);
        }
        return this.refreshHelpdeskTicketFeedback(employeeId);
      }),
      catchError(() => this.refreshHelpdeskTicketFeedback(employeeId))
    );
  }

  refreshHelpdeskTicketFeedback(employeeId: number): Observable<THelpdeskTicketFeedback[]> {
    const { domainCode } = this.logOnUserDetails();
    return this.fetchHelpdeskTicketFeedback(employeeId).pipe(
      tap(data => {
        this.store.dispatch(loadHelpdeskTicketFeedbackSuccess({
          data,
          storename: EGlobalStateStoreNames.HelpdeskTicketFeedback,
          versionId: this.versionhash.get(EGlobalStateProcessId.HelpdeskTicketFeedback) ?? "1",
          domainCode,
          employeeId
        }));
      })
    );
  }


}

